import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Thermometer, Sun, Grid } from 'lucide-react';
import Seo from '../components/Seo';
import { PAGE_COPY } from '../data/seoContent';
import { Link } from 'react-router-dom';
import { SAUNAS } from '../data/products';
import { itemListLd } from '../data/structuredData';
import { SAUNALIFE_SAUNAS, SAUNA_HEATERS } from '../data/outdoorCatalogs';
import CallForPricingGrid from '../components/CallForPricingGrid';
import ProductGrid from '../components/ProductGrid';
import { VideoBlock } from '../components/VideoBlock';
import { VIDEOS } from '../data/mediaAssets';
import { REELS } from '../data/reels';
import SocialPostBanner from '../components/SocialPostBanner';

const SaunasPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const infraredSaunas = SAUNAS.filter(s => s.category === 'infrared');
  const redLight = SAUNAS.filter(s => s.category === 'redlight');

  const getFilteredProducts = () => {
    switch (activeCategory) {
      case 'infrared':
        return infraredSaunas;
      case 'redlight':
        return redLight;
      default:
        return SAUNAS;
    }
  };

  const categories = [
    { id: 'all', label: 'All Saunas', icon: Grid, count: SAUNAS.length },
    { id: 'infrared', label: 'Infrared Saunas', icon: Thermometer, count: infraredSaunas.length },
    { id: 'redlight', label: 'Red Light Therapy', icon: Sun, count: redLight.length },
  ];

  const filteredProducts = getFilteredProducts();

  return (
    <>
      <Seo page="/saunas" jsonLd={itemListLd('Saunas in Naples, FL', '/saunas', [
        ...SAUNAS.map((p) => ({ brand: p.brand, name: p.name, url: `/products/${p.id}`, image: p.image })),
        ...SAUNALIFE_SAUNAS.map((p) => ({ brand: p.brand, name: p.name, image: p.image })),
      ])} />

      <div className="pt-36 pb-20" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 20%, #AFEEEE 50%, #98FB98 80%, #F0FFF0 100%)' }} data-testid="saunas-page">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-[#F88200] uppercase tracking-wider">Health Mate® Smart Infrared</span>
            </div>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-6xl font-bold uppercase text-[#0E3D95] mb-4">
              Saunas in Naples, FL
            </h1>
            <p className="text-lg text-slate-600 max-w-4xl" data-testid="page-intro">
              {PAGE_COPY.saunas} Also explore our{' '}
              <Link to="/leisurecraft" className="text-[#F88200] font-semibold hover:underline">LeisureCraft outdoor saunas, cold plunges & outdoor furniture</Link>.
            </p>
          </motion.div>

          {/* Serenity Sauna Video */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
            data-testid="sauna-video"
          >
            <div className="rounded-2xl overflow-hidden shadow-xl bg-black">
              <video
                autoPlay
                muted
                loop
                playsInline
                controls
                className="w-full h-auto max-h-[70vh] object-contain bg-black"
              >
                <source src="https://customer-assets-jt897jd0.emergentagent.net/job_product-pricing-27/artifacts/g2334735_Serenity%20Video%20%5B4K%5D_compressed.mp4" type="video/mp4" />
              </video>
            </div>
          </motion.div>

          {/* Sauna vs Infrared vs Red Light Comparison */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12 rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-white"
            data-testid="sauna-comparison"
          >
            <div className="bg-[#0B0F1A] text-white p-6 md:p-8">
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-black uppercase leading-none">
                Sauna vs Infrared <span className="text-[#F88200]">vs Red Light</span>
              </h2>
              <p className="text-slate-400 mt-1">The simple version, without the hype.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-0">
              {[
                {
                  label: 'Traditional', color: '#F88200', temp: '160–190°F', sub: 'Heats the air',
                  points: ['Heater + rocks warm the room', 'Pour water for steam', 'Hard sweat, classic Finnish'],
                },
                {
                  label: 'Infrared', color: '#2E75C0', temp: '120–140°F', sub: 'Heats your body, not the air',
                  points: ['Panels warm you, not the air', 'Gentler — sit 30–40 minutes', 'Easiest if big heat is too much'],
                },
                {
                  label: 'Red Light', color: '#B32B2B', temp: 'No heat', sub: 'Not a sauna',
                  points: ['Red + near-infrared light', 'No sweating, a few minutes', 'Used for skin and recovery'],
                },
              ].map((col) => (
                <div key={col.label} className="p-6 md:p-8 border-t-4 border-l border-slate-100 first:border-l-0" style={{ borderTopColor: col.color }}>
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mb-2" style={{ color: col.color }}>
                    {col.label}
                  </h3>
                  <p className="text-sm text-slate-500 mb-2">{col.sub}</p>
                  <p className="font-['Barlow_Condensed'] text-3xl font-black text-[#0E3D95] mb-4">{col.temp}</p>
                  <ul className="space-y-2">
                    {col.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-2 text-slate-600 text-sm">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: col.color }} />
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="px-6 md:px-8 pt-6 pb-4 border-t border-slate-100">
              <p className="font-['Barlow_Condensed'] text-lg md:text-xl font-bold uppercase text-[#0E3D95]">
                2–3 Sessions a Week, 15–20 Minutes
              </p>
              <p className="text-sm text-slate-600 mt-1">
                Linked to lower risk of cardiovascular disease and better overall health. Longer sessions did better than short ones. <span className="text-slate-400">(JAMA Internal Medicine, 2015 · 20-year study)</span>
              </p>
            </div>
            <div className="bg-[#0B0F1A] text-white px-6 md:px-8 py-4">
              <p className="font-['Barlow_Condensed'] font-bold uppercase tracking-wide">Leisure Wellness Naples</p>
              <p className="text-slate-400 text-sm">6000 Taylor Rd Ste 8, Naples · Sit in both and pick your heat.</p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <div className="flex flex-wrap gap-2 md:gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 md:px-5 md:py-3 font-semibold text-sm md:text-base transition-all rounded-lg ${
                    activeCategory === cat.id
                      ? 'bg-[#0E3D95] text-white shadow-lg'
                      : 'bg-white/80 text-[#0E3D95] hover:bg-[#F88200] hover:text-white border border-slate-200'
                  }`}
                  data-testid={`category-btn-${cat.id}`}
                >
                  <cat.icon size={18} />
                  <span>{cat.label}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeCategory === cat.id ? 'bg-white/20' : 'bg-slate-100'
                  }`}>
                    {cat.count}
                  </span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Results Count */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-6"
          >
            <p className="text-slate-600">
              Showing <span className="font-semibold text-[#0E3D95]">{filteredProducts.length}</span> products
              {activeCategory !== 'all' && (
                <span> in <span className="font-semibold text-[#F88200]">{categories.find(c => c.id === activeCategory)?.label}</span></span>
              )}
            </p>
          </motion.div>

          {/* Product Grid */}
          <ProductGrid products={filteredProducts} linkPrefix="/products" />

          {/* Sauna videos */}
          <VideoBlock
            eyebrow="See It In Action"
            title="Sauna Life at Leisure Wellness"
            subtitle="Step inside the sauna experience we bring to Naples backyards."
            src={VIDEOS.saunas}
            reel={REELS.saunas}
            testId="saunas-video"
          />
          <VideoBlock
            eyebrow="The Benefits of Sauna"
            title="Joe Rogan on Sauna Benefits"
            subtitle="Why regular sauna use is one of the best things you can do for recovery, heart health and longevity."
            src={VIDEOS.joeRoganSauna}
            reel={REELS.rogan}
            testId="joe-rogan-sauna-video"
          />
          <VideoBlock
            eyebrow="Health Mate Infrared Saunas"
            reel={REELS.serenity}
            testId="serenity-sauna-video"
          />
          <VideoBlock
            eyebrow="For Airbnb & Rental Hosts"
            reel={REELS.airbnb}
            testId="airbnb-sauna-video"
          />

          {/* Matched brand graphics */}
          <SocialPostBanner ids={['camel-sauna', 'monkey']} />

          {/* SaunaLife Outdoor Saunas */}
          <CallForPricingGrid
            testId="saunalife-saunas"
            eyebrow="SaunaLife · Backyard Wellness"
            title="SaunaLife Outdoor Saunas"
            subtitle="Premium outdoor Garden, Garden Luxury & Cube Luxury series saunas — traditional high-heat bathing built for year-round backyard use."
            items={SAUNALIFE_SAUNAS}
          />

          {/* Sauna Heaters */}
          <CallForPricingGrid
            testId="sauna-heaters"
            eyebrow="Electric & Wood-Burning"
            title="Sauna Heaters"
            subtitle="The heart of your sauna. Electric and wood-burning heaters from Huum, Harvia, Homecraft, Narvi & more — sized for any room."
            items={SAUNA_HEATERS}
          />

          {/* Info Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 bg-white/80 backdrop-blur-sm p-8 rounded-lg border border-slate-200"
          >
            <h2 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase text-[#0E3D95] mb-4">
              Health Mate® Smart Infrared Saunas
            </h2>
            <div className="grid md:grid-cols-2 gap-6 text-slate-600">
              <div>
                <h3 className="font-semibold text-[#0E3D95] mb-2">Full-Spectrum Tecoloy® Technology</h3>
                <p className="mb-4">
                  Every Health Mate sauna uses patented Tecoloy® medical-grade heaters delivering near-, mid-, and
                  far-infrared wavelengths for complete 360° body coverage — all rigorously tested for safe,
                  low electromagnetic frequency levels.
                </p>
                <h3 className="font-semibold text-[#0E3D95] mb-2">Smart & Connected</h3>
                <p>
                  Preheat and control your session from the app, enjoy 9-color near-infrared chromotherapy, and
                  stream your playlist through the built-in Bluetooth audio system.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-[#0E3D95] mb-2">Shipping & White Glove Installation</h3>
                <p className="mb-4">
                  Health Mate saunas ship separately (free shipping not included). Professional White Glove
                  Installation is available — contact us for shipping and installation pricing.
                </p>
                <h3 className="font-semibold text-[#0E3D95] mb-2">Contact for Pricing</h3>
                <p>
                  Pricing is available by request. Call or send us a message and our team will help you choose the
                  right model for your space and wellness goals.
                </p>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </>
  );
};

export default SaunasPage;
